import { useState, useEffect, useRef, useCallback } from "react";
import { useWatch } from "react-hook-form";
import { Checkbox, Skeleton, Stack, Typography } from "@mui/material";
import {
  FormButtonGroup,
  FormDateInput,
  FormSelectInput,
  FormTextInput,
} from "../../../../components/form";
import { RequiredField } from "../../../schedule/components/RequiredField";
import { useRegex } from "../../../../utils/useRegex";
import { useFormatter } from "../../../../utils/useFormatter";
import { Steps } from "../../types";
import { GetRelatedPerson } from "@/services/fhir/relatedperson/GetRelatedPerson";
import { fetchPatientRelatedPersons } from "@/services/fhir/relatedperson/relatedPersonVerification";
import { useRelatedPersonVerification } from "@/hooks/useRelatedPersonVerification";
import { RelatedPersonVerificationList } from "@/components/schedule/RelatedPersonVerificationList";
import { currentFeatures } from "@/configs/Settings";
import { VetTutorSearch } from "./VetTutorSearch";
import { VetTutorForm } from "./VetTutorForm";
import { VetPetList } from "./VetPetList";
import { VetPetFormWrapper } from "./VetPetFormWrapper";
import { IPetFormData } from "@/fluxvet/components/PetFormModal";
import { GetPatient, PostPatient } from "@/services/fhir";
import { toast } from "react-toastify";
import {
  CreateVetPatientWithRelation,
  ITutorData,
} from "@/services/fhir/vetpatient/CreateVetPatientWithRelation";
import { SearchCPF, toSerproNascimento } from "@/services/scim";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import dayjs from "dayjs";
import { EVOLUTION_TYPE_SERVICE } from "@/components/schedule/evolution/constants";

interface IProps {
  control: any;
  setModalOpen: any;
  cpfT: any;
  walkin: any;
  setPage: any;
  handleSubmit: any;
  setValue: any;
  /** Quando definido (ex.: CTA de evolução), pré-seleciona e trava o tipo. */
  lockTypeService?: string;
}

export function SelectAppointmentPatient({
  control,
  setModalOpen,
  cpfT,
  walkin,
  setPage,
  handleSubmit,
  setValue,
  lockTypeService,
}: IProps) {
  const { organization } = useOrganizationContext();
  const [showOtherResponsible, setShowOtherResponsible] = useState(false);
  const [informResponsible, setInformResponsible] = useState(false);

  const [relatedData, setRelatedData] = useState<any[] | undefined>();
  const [inactiveRelated, setInactiveRelated] = useState<any[]>([]);
  const [dismissedRelatedIds, setDismissedRelatedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const fetchRelatedRequestRef = useRef(0);
  const dismissedRelatedIdsRef = useRef<string[]>([]);

  // Estados para o fluxo veterinário
  const [vetFlow, setVetFlow] = useState<
    "search" | "createTutor" | "selectPet" | "createPet"
  >("search");
  const [tutorData, setTutorData] = useState<any>(null);
  const [petsList, setPetsList] = useState<any[]>([]);
  const [selectedPetId, setSelectedPetId] = useState<string | undefined>();
  const [showPetModal, setShowPetModal] = useState(false);

  // Estados para cadastro de tutor (Serpro)
  const [serproResponse, setSerproResponse] = useState<any>(null);

  // Opções de tipo de serviço
  const typeOptionsService: Array<any> = [
    { display: "Checkup", value: "CHECKUP" },
    { display: "Emergência", value: "EMERGENCY" },
    { display: "Retorno", value: "FOLLOWUP" },
    { display: "Rotina", value: "ROUTINE" },
    { display: "Encaixe", value: "WALKIN" },
    { display: "Primeiro atendimento", value: "FIRSTAPPOINTMENT" },
    { display: "Evolução", value: EVOLUTION_TYPE_SERVICE },
  ];

  const KinshipOptions = [
    { display: "Pai", value: "father" },
    { display: "Mãe", value: "mother" },
    { display: "Responsável", value: "legalDependent" },
  ];

  useEffect(() => {
    if (walkin) {
      setValue("typeService", "WALKIN");
    } else if (lockTypeService) {
      setValue("typeService", lockTypeService);
    }
  }, [walkin, lockTypeService, setValue]);

  let cpfValue = useWatch({ name: "cpf", control });
  let cpfResponsible = useWatch({ name: "cpfResponsible", control });
  let typeService = useWatch({ name: "typeService", control });
  let tutorBirthDate = useWatch({ name: "tutorBirthDate", control });

  // INVERSÃO SEMÂNTICA APENAS para defaultApp
  // Quando é defaultApp=TRUE (app de saúde humana):
  //   - campo "cpf" → semanticamente representa o PACIENTE
  //   - campo "cpfResponsible" → semanticamente representa o RESPONSÁVEL
  //   → MAS internamente precisamos inverter para as APIs funcionarem
  //
  // Quando é defaultApp=FALSE (app veterinário):
  //   - campo "cpf" → CPF do tutor (patient na lógica interna)
  //   - campo "cpfResponsible" → ID do pet (responsible na lógica interna)
  //   → Já vem correto, NÃO invertemos
  const actualPatientCpf = currentFeatures?.defaultApp
    ? cpfValue
    : cpfResponsible;
  const actualResponsibleCpf = currentFeatures?.defaultApp
    ? cpfResponsible
    : cpfValue;

  const isPatientCpfValid = Boolean(
    cpfValue?.length === 14 && useRegex.cpf.test(cpfValue),
  );

  const hasNoRelatedFound =
    !loading &&
    isPatientCpfValid &&
    (relatedData?.length ?? 0) === 0 &&
    inactiveRelated.length === 0;

  const isResponsibleSectionRequired =
    (relatedData?.length ?? 0) > 0 ||
    inactiveRelated.length > 0 ||
    informResponsible;

  const showOtherResponsibleRef = useRef(false);

  useEffect(() => {
    showOtherResponsibleRef.current = showOtherResponsible;
  }, [showOtherResponsible]);

  const autoSelectSingleActiveResponsible = useCallback(
    (activeRelated: any[]) => {
      if (activeRelated.length !== 1 || showOtherResponsibleRef.current) {
        return;
      }

      const identifier = activeRelated[0]?.resource?.identifier?.[0]?.value;
      if (!identifier) return;

      setValue("cpfResponsible", useFormatter.formatCPF(identifier));
    },
    [setValue],
  );

  const clearResponsibleFields = useCallback(() => {
    setRelatedData(undefined);
    setInactiveRelated([]);
    setDismissedRelatedIds([]);
    dismissedRelatedIdsRef.current = [];
    setShowOtherResponsible(false);
    setInformResponsible(false);
    setValue("cpfResponsible", undefined);
    setValue("relationship", undefined);
  }, [setValue]);

  const fetchRelatedPersons = useCallback(
    async (cpf: string) => {
      const requestId = ++fetchRelatedRequestRef.current;
      setLoading(true);
      try {
        const { activeRelated, inactiveRelated } =
          await fetchPatientRelatedPersons(cpf, dismissedRelatedIdsRef.current);

        if (requestId !== fetchRelatedRequestRef.current) return;

        setRelatedData(activeRelated);
        setInactiveRelated(inactiveRelated);
        autoSelectSingleActiveResponsible(activeRelated);
      } catch {
        if (requestId !== fetchRelatedRequestRef.current) return;
        setRelatedData([]);
        setInactiveRelated([]);
      } finally {
        if (requestId === fetchRelatedRequestRef.current) {
          setLoading(false);
        }
      }
    },
    [autoSelectSingleActiveResponsible],
  );

  const patientIdNormalized = cpfValue?.replace(/[.-]/g, "") ?? "";

  const {
    isRowLoading,
    isVerifyLoading,
    isDenyLoading,
    handleVerify,
    handleDeny,
    isVerificationInProgress,
  } = useRelatedPersonVerification({
    patientId: patientIdNormalized,
    onAfterVerify: async () => {
      if (cpfValue) {
        await fetchRelatedPersons(cpfValue);
      }
    },
    onAfterDeny: async (_entry, resourceId) => {
      if (!dismissedRelatedIdsRef.current.includes(resourceId)) {
        dismissedRelatedIdsRef.current = [
          ...dismissedRelatedIdsRef.current,
          resourceId,
        ];
        setDismissedRelatedIds(dismissedRelatedIdsRef.current);
      }
      if (cpfValue) {
        await fetchRelatedPersons(cpfValue);
      }
    },
  });

  // Busca automática de responsáveis quando o CPF do paciente estiver válido (debounce 400ms).
  useEffect(() => {
    if (!currentFeatures?.defaultApp) return;

    if (!isPatientCpfValid) {
      clearResponsibleFields();
      return;
    }

    dismissedRelatedIdsRef.current = [];
    setDismissedRelatedIds([]);

    const timeoutId = setTimeout(() => {
      fetchRelatedPersons(cpfValue);
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [
    cpfValue,
    isPatientCpfValid,
    clearResponsibleFields,
    fetchRelatedPersons,
  ]);

  // Handlers para o fluxo veterinário
  const handleSearchTutor = async () => {
    // Validação: CPF obrigatório
    if (!cpfValue || cpfValue.length !== 14) {
      toast.warn("Por favor, digite um CPF válido");
      return;
    }

    // Validação: Tipo de atendimento obrigatório
    if (!typeService) {
      toast.warn(
        "Por favor, selecione o tipo de atendimento antes de continuar",
      );
      return;
    }

    setLoading(true);
    try {
      // Passo 1: Busca o tutor pelo CPF no sistema
      const tutorResponse = await GetPatient({
        identifier: cpfValue.replace(/[.-]/g, ""),
      });

      if (tutorResponse?.data) {
        // Tutor encontrado no sistema
        setTutorData(tutorResponse.data);
        setSerproResponse(null); // Limpa dados do Serpro
        toast.success("Tutor encontrado!");

        // Busca os pets relacionados ao tutor
        const petsResponse = await GetRelatedPerson({
          includePatient: true,
          identifier: cpfValue.replace(/[.-]/g, ""),
          vetRelated: true,
          active: true,
        });

        if (petsResponse?.data?.entry) {
          // Filtra apenas os recursos do tipo Patient (pets)
          // O retorno inclui RelatedPerson e Patient, queremos apenas os Patient
          const patientsOnly = petsResponse.data.entry.filter(
            (entry: any) => entry.resource.resourceType === "Patient",
          );
          setPetsList(patientsOnly);
        } else {
          setPetsList([]);
        }

        // Vai para a tela de seleção de pet
        setVetFlow("selectPet");
        return;
      }
    } catch (error) {
      console.error("Erro ao buscar tutor no sistema:", error);
    } finally {
      setLoading(false);
    }

    // Passo 2: Se não encontrou no sistema, busca no Serpro (V3: CPF + nascimento)
    try {
      const nascimento = toSerproNascimento(tutorBirthDate);
      if (!nascimento) {
        toast.warn(
          "Informe a data de nascimento do tutor para validar o CPF.",
        );
        setLoading(false);
        return;
      }

      const serproSearch = await SearchCPF(
        cpfValue.replace(/[.-]/g, ""),
        nascimento,
      );

      if (serproSearch?.status === 400) {
        toast.warn(
          "O número de CPF do tutor não é válido. Por favor verifique se os dados estão corretos e tente novamente!",
        );
        setLoading(false);
        return;
      }

      if (serproSearch?.status === 422) {
        toast.warn(
          "O CPF informado pertence a um menor de idade. Por favor, informe o CPF de um responsável maior de idade.",
        );
        setLoading(false);
        return;
      }

      // CPF válido no Serpro, vai para cadastro
      toast.info("Tutor não encontrado. É necessário realizar o cadastro.");
      setSerproResponse(serproSearch?.data);
      setVetFlow("createTutor");
    } catch (error) {
      console.error("Erro ao validar CPF no Serpro:", error);
      toast.error("Erro ao validar CPF do tutor. Por favor, tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTutor = async (data: any) => {
    if (!serproResponse) {
      toast.error("Dados do Serpro não encontrados");
      return;
    }

    setLoading(true);
    try {
      // Prepara o body para criar o Patient (tutor) via MPI
      const body = {
        name: serproResponse?.nome || data?.tutorName,
        identifier: serproResponse?.ni || cpfValue.replace(/[.-]/g, ""),
        birthDate: dayjs(serproResponse?.nascimento, "DDMMYYYY").isValid()
          ? dayjs(serproResponse?.nascimento, "DDMMYYYY").format("YYYY-MM-DD")
          : undefined,
        phoneNumber: data?.tutorPhoneNumber?.replace(/\D/g, ""),
        managingOrganization: organization?.id,
        gender: data?.tutorGender,
      };

      // Cria o Patient (tutor) no MPI
      const mpiResponse = await PostPatient(body);

      if (mpiResponse?.data) {
        toast.success("Tutor cadastrado com sucesso!");

        // Seta os dados do tutor criado
        setTutorData(mpiResponse.data);

        // Limpa o serproResponse
        setSerproResponse(null);

        // Inicializa lista de pets vazia (tutor novo não tem pets)
        setPetsList([]);

        // Vai para a tela de seleção de pet
        setVetFlow("selectPet");
      } else {
        toast.error("Erro ao cadastrar tutor");
      }
    } catch (error) {
      console.error("Erro ao criar tutor:", error);
      toast.error("Erro ao cadastrar tutor. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPet = (petId: string) => {
    setSelectedPetId(petId);
  };

  const handleAddNewPet = () => {
    setShowPetModal(true);
  };

  const handleSubmitPet = async (petData: IPetFormData) => {
    if (!tutorData) {
      toast.error("Dados do tutor não encontrados");
      return;
    }

    setLoading(true);
    try {
      // Prepara os dados do tutor no formato esperado pelo serviço
      const tutorDataForService: ITutorData = {
        cpf: cpfValue,
        name: tutorData.name?.[0]?.text || "",
        phoneNumber:
          tutorData.telecom?.find((t: any) => t.system === "phone")?.value ||
          "",
        gender: tutorData.gender || "",
      };

      // Chama o serviço que cria o pet e vincula ao tutor
      const result = await CreateVetPatientWithRelation(
        petData,
        tutorDataForService,
      );

      if (result.success && result.petId) {
        toast.success(`Pet "${result.petName}" cadastrado com sucesso!`);

        // Fecha o modal
        setShowPetModal(false);

        // Recarrega a lista de pets do tutor
        await reloadPetsList();

        // Seleciona automaticamente o pet recém-criado
        setSelectedPetId(result.petId);
      } else {
        toast.error(result.error || "Erro ao cadastrar pet");
      }
    } catch (error) {
      console.error("Erro ao cadastrar pet:", error);
      toast.error("Erro ao cadastrar pet. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  // Função auxiliar para recarregar a lista de pets
  const reloadPetsList = async () => {
    try {
      const petsResponse = await GetRelatedPerson({
        includePatient: true,
        identifier: cpfValue.replace(/[.-]/g, ""),
        vetRelated: true,
        active: true,
      });

      if (petsResponse?.data?.entry) {
        // Filtra apenas os recursos do tipo Patient (pets)
        const patientsOnly = petsResponse.data.entry.filter(
          (entry: any) => entry.resource.resourceType === "Patient",
        );
        setPetsList(patientsOnly);
      }
    } catch (error) {
      console.error("Erro ao recarregar lista de pets:", error);
    }
  };

  const handleContinueWithPet = () => {
    // Validação: Pet selecionado
    if (!selectedPetId) {
      toast.warn("Por favor, selecione um pet");
      return;
    }

    // Validação: Tipo de atendimento obrigatório
    if (!typeService) {
      toast.warn(
        "Por favor, selecione o tipo de atendimento antes de continuar",
      );
      return;
    }

    // Preenche os campos necessários para continuar o fluxo
    setValue("cpfResponsible", selectedPetId);

    // Continua para o próximo passo
    if (actualResponsibleCpf) {
      setPage(Steps.S2CreateResponsible);
    } else if (actualPatientCpf || cpfValue) {
      setPage(Steps.S3CreatePatient);
    }
  };

  const onSubmit = () => {
    // Se tem CPF do responsável (actualResponsibleCpf), vai para criar responsável
    // Senão, vai direto para criar paciente
    if (actualResponsibleCpf) {
      setPage(Steps.S2CreateResponsible);
    } else if (actualPatientCpf || cpfValue) {
      setPage(Steps.S3CreatePatient);
    }
  };

  // Renderização condicional baseada no tipo de app
  if (!currentFeatures?.defaultApp) {
    // Fluxo veterinário
    return (
      <Stack spacing={2}>
        {/* Etapa 1: Buscar tutor */}
        {vetFlow === "search" && (
          <>
            <VetTutorSearch
              control={control}
              cpfValue={cpfValue}
              onSearchTutor={handleSearchTutor}
              loading={loading}
            />

            {/* Tipo de atendimento */}
            <Stack>
              <Typography variant="h6" fontWeight="600" color="neutral600.main">
                Atendimento <RequiredField />
              </Typography>
              <Typography variant="body1">
                Escolha o tipo de atendimento dessa consulta.
              </Typography>
            </Stack>

            <FormSelectInput
              name="typeService"
              defaultValue={
                walkin ? "WALKIN" : lockTypeService ? lockTypeService : ""
              }
              control={control}
              label="Tipo de atendimento"
              selectOptions={typeOptionsService}
              required
              readOnly={Boolean(lockTypeService)}
            />

            <FormButtonGroup
              goBackButtonText="Fechar"
              onGoBackButtonClick={() => setModalOpen(false)}
              nextButtonText="Próximo"
              onNextButtonClick={handleSearchTutor}
              // disabled={true}
            />
          </>
        )}

        {/* Etapa 2: Cadastrar tutor */}
        {vetFlow === "createTutor" && (
          <>
            <VetTutorForm
              control={control}
              cpf={cpfValue}
              onGoBack={() => {
                setVetFlow("search");
                setSerproResponse(null); // Limpa dados do Serpro ao voltar
              }}
              onSubmit={handleCreateTutor}
              serproResponse={serproResponse}
              loading={loading}
            />

            {/* Tipo de atendimento - sempre visível */}
          </>
        )}

        {/* Etapa 3: Selecionar pet */}
        {vetFlow === "selectPet" && (
          <>
            <VetPetList
              pets={petsList.map((entry: any) => {
                // Agora entry.resource é diretamente um Patient (pet)
                const patient = entry.resource;

                // Busca a extensão patient-animal que contém as informações do pet
                const animalExtension = patient.extension?.find((ext: any) =>
                  ext.url?.includes("patient-animal"),
                );

                return {
                  id: patient.id || "",
                  name: patient.name?.[0]?.text || "",
                  species: animalExtension?.extension?.find(
                    (ext: any) => ext.url === "species",
                  )?.valueCodeableConcept?.coding?.[0]?.display,
                  breed: animalExtension?.extension?.find(
                    (ext: any) => ext.url === "breed",
                  )?.valueCodeableConcept?.coding?.[0]?.display,
                  sex: patient.gender,
                  photoUrl: patient.photo?.[0]?.data
                    ? `data:${patient.photo[0].contentType};base64,${patient.photo[0].data}`
                    : undefined,
                };
              })}
              selectedPetId={selectedPetId}
              onSelectPet={handleSelectPet}
              onAddNewPet={handleAddNewPet}
              onGoBack={() => setVetFlow("search")}
              onContinue={handleContinueWithPet}
            />

            {/* Modal de cadastro de pet */}
            <VetPetFormWrapper
              open={showPetModal}
              onClose={() => setShowPetModal(false)}
              onSubmit={handleSubmitPet}
              loading={loading}
            />
          </>
        )}
      </Stack>
    );
  }

  // Fluxo padrão (saúde humana)
  return (
    <Stack spacing={2}>
      <Stack>
        <Typography variant="h6" fontWeight="600" color="neutral600.main">
          Paciente <RequiredField />
        </Typography>
        <Typography variant="body1">
          Digite o CPF do paciente que deseja preencher a vaga.
        </Typography>
        <FormTextInput
          label="CPF"
          name="cpf"
          control={control}
          required={true}
          pattern={!cpfT && useRegex.cpf}
          mask={useFormatter.formatCPF}
          maxLength={14}
          autoFocus
        />
      </Stack>
      {isPatientCpfValid && (
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight="600" color="neutral600.main">
            Responsável legal
            {isResponsibleSectionRequired ? <RequiredField /> : null}
          </Typography>
          <Typography variant="body1">
            {relatedData?.length
              ? "Selecione o responsável legal."
              : inactiveRelated?.length
                ? "Valide os responsáveis pendentes ou informe outro CPF."
                : hasNoRelatedFound
                  ? informResponsible
                    ? "Digite o CPF do responsável legal."
                    : "Marque a opção abaixo caso deseje informar um responsável legal."
                  : "Digite o CPF do responsável legal."}
          </Typography>
          {loading ? (
            <Skeleton variant="rounded" height={50} />
          ) : (
            <>
              <RelatedPersonVerificationList
                entries={inactiveRelated}
                isRowLoading={isRowLoading}
                isVerifyLoading={isVerifyLoading}
                isDenyLoading={isDenyLoading}
                onVerify={handleVerify}
                onDeny={handleDeny}
                title="Responsáveis pendentes de verificação"
              />

              {relatedData && relatedData.length > 0 && (
                <Stack spacing={2}>
                  {!showOtherResponsible && (
                    <FormSelectInput
                      name="cpfResponsible"
                      control={control}
                      label="Nome do responsável"
                      selectOptions={relatedData.map((related: any) => ({
                        display: related.resource.name[0].text,
                        value: useFormatter.formatCPF(
                          related.resource.identifier[0].value,
                        ),
                      }))}
                      required
                    />
                  )}

                  {showOtherResponsible && (
                    <Stack direction="row" spacing={2}>
                      <FormTextInput
                        label="CPF"
                        name="cpfResponsible"
                        control={control}
                        required={true}
                        pattern={useRegex.cpf}
                        mask={useFormatter.formatCPF}
                        maxLength={14}
                        autoFocus
                      />
                      <FormSelectInput
                        control={control}
                        name="relationship"
                        label="Grau de parentesco"
                        selectOptions={KinshipOptions}
                        required
                      />
                    </Stack>
                  )}

                  <Stack direction="row" alignItems="center" width="100%">
                    <Checkbox
                      checked={showOtherResponsible}
                      onChange={(e) => {
                        setShowOtherResponsible(e.target.checked);
                        if (e.target.checked) {
                          setValue("cpfResponsible", undefined);
                          setValue("relationship", undefined);
                        }
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                    <Typography variant="body1">Outro responsável</Typography>
                  </Stack>
                </Stack>
              )}

              {hasNoRelatedFound && (
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" width="100%">
                    <Checkbox
                      checked={informResponsible}
                      onChange={(e) => {
                        setInformResponsible(e.target.checked);
                        if (!e.target.checked) {
                          setValue("cpfResponsible", undefined);
                          setValue("relationship", undefined);
                        }
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                    <Typography variant="body1">
                      O paciente possui responsável legal
                    </Typography>
                  </Stack>

                  {informResponsible && (
                    <Stack direction="row" spacing={2}>
                      <FormTextInput
                        label="CPF"
                        name="cpfResponsible"
                        control={control}
                        required={true}
                        pattern={useRegex.cpf}
                        mask={useFormatter.formatCPF}
                        maxLength={14}
                        autoFocus
                      />
                      <FormSelectInput
                        control={control}
                        name="relationship"
                        label="Grau de parentesco"
                        selectOptions={KinshipOptions}
                        required
                      />
                    </Stack>
                  )}
                </Stack>
              )}
            </>
          )}
        </Stack>
      )}

      <Stack>
        <Typography variant="h6" fontWeight="600" color="neutral600.main">
          Atendimento <RequiredField />
        </Typography>
        <Typography variant="body1">
          Escolha o tipo de atendimento dessa consulta.
        </Typography>
      </Stack>

      <FormSelectInput
        name="typeService"
        defaultValue={
          walkin ? "WALKIN" : lockTypeService ? lockTypeService : ""
        }
        control={control}
        label="Tipo de atendimento"
        selectOptions={typeOptionsService}
        required
        readOnly={Boolean(lockTypeService)}
      />

      <FormButtonGroup
        goBackButtonText="Fechar"
        onGoBackButtonClick={() => setModalOpen(false)}
        onNextButtonClick={handleSubmit(onSubmit)}
        disabled={isVerificationInProgress || inactiveRelated.length > 0}
      />
    </Stack>
  );
}
